import { isNil } from 'lodash';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { ANIMATION_DURATION } from '@optimus-web/common';
import {
    ActivableRenderer,
    getDisableClickAwayProps,
    Portal,
    useClickAway,
    useId,
    useValueAsRef,
} from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { VisuallyHidden } from '../helpers';
import { LoadingOverlay } from '../helpers/LoadingOverlay';
import { useModalFocus, useLockScroll } from '../hooks';
import { Spinner } from '../Spinner';
import { Stack, StackItem } from '../Stack';
import { Typography } from '../typography';
import { IconButton } from '../IconButton';
import { DialogFooter } from './DialogFooter';
import {
    ActionsButtonsStack,
    BackdropElement,
    DialogActionsElement,
    DialogButtonElement,
    DialogContainerElement,
    DialogContentElement,
    DialogElement,
    DialogFooterWithDetail,
    DialogHeader,
    DialogTitle,
    FooterDetailContainer,
} from './style';
import { DialogProps } from './types';

const DialogActivatorFunction = ActivableRenderer<React.Component<DialogProps>, DialogProps>({
    delay: ANIMATION_DURATION.default,
});

const DialogComponent = ({
    size,
    modal = true,
    active,
    position,
    contentWithoutPadding,
    actions,
    footerDetail,
    portalContainerId,
    onClose,
    title,
    subtitle,
    children,
    anchorElement,
    defaultFocusElement,
    noMaxWidth = false,
    mobileFullscreen = false,
    loading,
    footer,
    fullWidthTitle = false,
    lockScroll,
    className,
    contentRef,
    ...other
}: DialogProps) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const clickAwayCloseHandler = useValueAsRef(onClose);
    const shouldIncludeCloseHandler = active && !loading && !!onClose;
    const closeButtonLabelIdRef = useId('dialog-close-label');
    const dialogTitleId = useId('dialog-title');
    const dialogSubtitleId = useId('dialog-subtitle');

    const { registerClickAway, deregisterClickAway } = useClickAway();
    const { t } = useLocalization();

    useModalFocus({
        open: active && !loading,
        componentRef: dialogRef,
        anchorElement,
        defaultFocusElement,
    });

    useLockScroll(active ?? false, lockScroll);
    useEffect(() => {
        if (shouldIncludeCloseHandler) {
            registerClickAway({
                handler: (e) => clickAwayCloseHandler.current?.(e),
                roots: [() => anchorElement, () => dialogRef.current],
            });
        }
        return () => {
            deregisterClickAway();
        };
    }, [anchorElement, clickAwayCloseHandler, deregisterClickAway, registerClickAway, shouldIncludeCloseHandler]);

    // handle screen height on iphone (without toolbars and keyboard)
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    const originalHeight = useRef('');

    const handleResize = useCallback(() => {
        // Skip height adjustment for unsupported browsers or mobile fullscreen dialogs
        if (!window.visualViewport || mobileFullscreen) return;
        window.document.body.style.height = `${window.visualViewport.height}px`;
        setWindowHeight(window.visualViewport.height);
        window.scrollTo(0, 0);
    }, [setWindowHeight, mobileFullscreen]);

    useEffect(() => {
        if (window.visualViewport != null && modal) {
            originalHeight.current = window.document.body.style.height;
            window.visualViewport.addEventListener('resize', handleResize);
        }
        return () => {
            if (window.visualViewport != null && modal) {
                window.visualViewport.removeEventListener('resize', handleResize);
                window.document.body.style.height = originalHeight.current;
            }
        };
    }, [handleResize, modal]);

    /*
     * TODO remove all of this once the migration is completed
     * see https://mews.atlassian.net/browse/DX-363
     * ⤵️
     */
    const actionsButtons =
        actions && actions.length > 0
            ? actions.map((action, index) => (
                  <DialogButtonElement
                      key={action.key || index}
                      data-test-button-action={action.key || index}
                      {...action}
                  />
              ))
            : null;

    const actionsElement =
        actionsButtons && isNil(footerDetail) ? (
            <DialogActionsElement>{actionsButtons}</DialogActionsElement>
        ) : (
            actionsButtons && (
                <ActionsButtonsStack
                    phoneOnly={{
                        vertical: true,
                    }}
                >
                    {actionsButtons}
                </ActionsButtonsStack>
            )
        );

    // To keep backwards compatibility with dialogFactory DialogActions, we need to not render the inner div.
    const FooterContainer = isNil(footerDetail) ? Fragment : DialogFooterWithDetail;

    const footerComponent =
        !isNil(footerDetail) || !isNil(actionsElement) ? (
            <FooterContainer>
                <FooterDetailContainer>{footerDetail}</FooterDetailContainer>
                {actionsElement}
            </FooterContainer>
        ) : null;

    const content = (
        <>
            {title || onClose ? (
                <DialogHeader
                    distribution="space-between"
                    itemAlignment={subtitle ? 'start' : 'center'}
                    position={position}
                    data-test-dialog-header
                >
                    <Stack vertical>
                        <DialogTitle
                            data-test-dialog-title
                            ellipsis
                            maxLines={3}
                            position={position}
                            fullWidth={fullWidthTitle}
                            id={dialogTitleId}
                        >
                            {title}
                        </DialogTitle>
                        {subtitle && (
                            <StackItem grow={fullWidthTitle}>
                                <Typography
                                    ellipsis
                                    maxLines={2}
                                    as="p"
                                    variant="tertiary"
                                    textStyle="bodySmall"
                                    id={dialogSubtitleId}
                                >
                                    {subtitle}
                                </Typography>
                            </StackItem>
                        )}
                    </Stack>
                    {onClose && (
                        <>
                            <VisuallyHidden id={closeButtonLabelIdRef} aria-hidden>
                                {t(OptimusTextKey.CloseDialog)} {title}
                            </VisuallyHidden>
                            <IconButton
                                icon={IconName.CrossClose}
                                onClick={onClose}
                                data-test-button-close
                                variant="tertiary"
                                size="small"
                                type="button"
                                aria-labelledby={closeButtonLabelIdRef}
                            />
                        </>
                    )}
                </DialogHeader>
            ) : null}

            <DialogContentElement
                position={position}
                ref={contentRef}
                withoutPadding={contentWithoutPadding}
                data-test-dialog-content
            >
                {children}
            </DialogContentElement>
            {footer ? <DialogFooter {...footer} /> : null}

            {/* TODO remove this once the migration is completed */}
            {!footer ? footerComponent : null}
            <LoadingOverlay show={loading}>
                <Spinner />
            </LoadingOverlay>
        </>
    );

    return (
        <Portal containerId={portalContainerId} {...other}>
            <DialogContainerElement
                height={windowHeight}
                {...(!onClose ? getDisableClickAwayProps() : {})}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? dialogTitleId : undefined}
                aria-describedby={subtitle ? dialogSubtitleId : undefined}
                {...other}
            >
                {active && modal && <BackdropElement />}
                {active && (
                    <DialogElement
                        noMaxWidth={noMaxWidth}
                        size={size}
                        position={position}
                        ref={dialogRef}
                        mobileFullscreen={mobileFullscreen}
                        data-test-dialog
                        className={className}
                    >
                        {content}
                    </DialogElement>
                )}
            </DialogContainerElement>
        </Portal>
    );
};

export const Dialog = DialogActivatorFunction(DialogComponent);
